import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles Google OAuth redirect: reads ?code=... from URL, posts to backend
 * which exchanges it for tokens, fetches profile, and sets session cookie.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GamerAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setGamer } = useMafiaAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const code = search.get('code');
    const googleErr = search.get('error');
    if (googleErr || !code) {
      setError(googleErr || 'Missing authorization code in callback URL');
      return;
    }
    // Guard against double-exchange (React StrictMode / refresh): codes are single-use
    const usedKey = `g_oauth_used_${code.slice(0, 16)}`;
    if (sessionStorage.getItem(usedKey)) return;
    sessionStorage.setItem(usedKey, '1');

    const redirectUri = window.location.origin + '/gamer/auth/callback';
    (async () => {
      try {
        const r = await fetch(`${API}/api/mafia/auth/google/exchange`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        });
        if (!r.ok) {
          let detail = `${r.status} ${r.statusText}`;
          try {
            const j = await r.json();
            detail = j.detail || detail;
          } catch { /* not json */ }
          throw new Error(detail);
        }
        const data = await r.json();
        setGamer && setGamer(data);
        navigate('/dashboard/card', { replace: true, state: { user: data } });
      } catch (e) {
        setError(e.message || 'Auth exchange failed');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div
        data-testid="gamer-auth-error"
        className="min-h-screen flex items-center justify-center bg-[#050505] p-6"
      >
        <div className="w-full max-w-lg border border-white/10 bg-[#0A0303] p-6">
          <h2 className="font-serif text-lg uppercase tracking-[0.3em] text-[#DC2626] font-bold">
            Sign-in failed
          </h2>
          <p className="text-sm text-[#A3A3A3] mt-3">We couldn't complete your Google sign-in.</p>
          <pre className="mt-4 p-3 bg-black/40 border border-white/10 text-[11px] text-rose-300 whitespace-pre-wrap break-all">
            {error}
          </pre>
          <p className="text-[11px] text-[#A3A3A3] mt-4 leading-relaxed">
            Common causes: missing <code>GOOGLE_CLIENT_ID</code> / <code>GOOGLE_CLIENT_SECRET</code> on the backend,
            redirect URI mismatch in Google Cloud Console, or the auth code was already used (refresh page to retry).
          </p>
          <Link
            to="/mafia/login"
            data-testid="gamer-auth-back-link"
            className="inline-block mt-5 px-4 py-2 text-[11px] uppercase tracking-[0.3em] font-semibold bg-[#DC2626] hover:bg-[#B91C1C] text-white"
          >
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="gamer-auth-callback"
      className="min-h-screen flex items-center justify-center bg-[#050505] text-[#A3A3A3] text-sm uppercase tracking-[0.3em]"
    >
      Signing you in…
    </div>
  );
}
